import { z } from 'zod';

/**
 * Field deployment jobs — the work order an engineer carries into the field,
 * and the evidence they bring back.
 *
 * ## Why this is not `jobs`
 *
 * `jobs` in the API schema is the platform's own async-work queue:
 * `device.action`, `site.apply`, `olt.provision`, with `progress` 0..100 and a
 * worker that moves a row queued → running → done. A deployment job is a work
 * order given to a PERSON. It lives for days, it has an assignee, it carries
 * photographs, and its most important state — `incomplete` — is one no worker
 * will ever resolve because only an administrator can. Sharing a table would
 * mean the queue's reapers had to learn to ignore a status that is not theirs,
 * which is how a subscriber's deployment record ends up swept.
 *
 * ## The rule this whole module exists to enforce
 *
 * An engineer may NEVER place equipment by typing a coordinate, because once
 * stored a typed coordinate is indistinguishable from a measured one. So there
 * is no manual-entry path anywhere — not in this contract, not on the API, not
 * in the database, where `field_job_captures.source` carries a CHECK
 * constraint pinning it to `device_fix`.
 *
 * The consequence is that a fix the device could not take accurately enough is
 * NOT an error to be worked around. It is recorded, the job goes `incomplete`,
 * and an administrator confirms and closes it as a distinct, audited act.
 */

// ---------------------------------------------------------------------------
// What a job points at
// ---------------------------------------------------------------------------

/**
 * The three things an engineer is sent to deploy.
 *
 * `splitter` covers ODF, enclosure and FAT, because that is how the platform
 * already models them — one `splitters` row with a `kind`. Whether photos are
 * required therefore depends on the kind of the row, not on this value; see
 * `PHOTO_REQUIRED_TARGETS`.
 */
export const fieldTargetTypeSchema = z.enum(['onu', 'splitter', 'zone']);
export type FieldTargetType = z.infer<typeof fieldTargetTypeSchema>;

/**
 * What a photograph is OF.
 *
 * Deliberately the physical thing, not "photo 1 / photo 2": an administrator
 * reviewing an incomplete job three weeks later needs to know whether the
 * missing picture was of the enclosure or of the unit inside it.
 */
export const fieldPhotoKindSchema = z.enum(['onu', 'odf', 'fat', 'other']);
export type FieldPhotoKind = z.infer<typeof fieldPhotoKindSchema>;

/**
 * The photo kind each deployment must produce, by what is being deployed.
 *
 * The operator's list, verbatim: an ONU, an ODF and a FAT. An enclosure and a
 * zone are not on it, and adding them here would silently start failing jobs
 * that are complete.
 */
export const PHOTO_REQUIRED_TARGETS: Record<string, FieldPhotoKind> = {
  onu: 'onu',
  odf: 'odf',
  fat: 'fat',
};

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * `incomplete` is a first-class state, not an error path.
 *
 * assigned    — created and given to an engineer.
 * in_progress — the engineer is on site.
 * complete    — submitted, and everything the target required was there: a fix
 *               inside the accuracy policy, plus the photo when one is due.
 * incomplete  — submitted, and something was refused or missing. The job holds
 *               what was captured, what it was judged against, and why.
 * closed      — an administrator confirmed an incomplete job and closed it.
 *               Terminal, and the only route out of `incomplete`.
 * cancelled   — an administrator called the job off while it was still open
 *               (`assigned` or `in_progress`). Terminal like `closed`; a soft
 *               state, so the record that somebody was sent survives rather
 *               than being deleted.
 */
export const fieldJobStatusSchema = z.enum([
  'assigned',
  'in_progress',
  'complete',
  'incomplete',
  'closed',
  'cancelled',
]);
export type FieldJobStatus = z.infer<typeof fieldJobStatusSchema>;

/**
 * Why a submission did not complete. One headline value; the full picture is
 * `FieldJob.requirements`, which is derived from the stored rows rather than
 * duplicated here so the two can never disagree.
 *
 * `gps_accuracy` is the case the operator's rule is about: a real fix arrived,
 * it was simply not good enough, and the platform keeps it as evidence rather
 * than discarding it or letting somebody round it into a placement.
 */
export const fieldIncompleteReasonSchema = z.enum([
  'gps_accuracy',
  'gps_no_fix',
  'gps_permission_denied',
  'gps_timeout',
  'photos_missing',
]);
export type FieldIncompleteReason = z.infer<typeof fieldIncompleteReasonSchema>;

/**
 * What the device had to say about where it was.
 *
 * `accepted` and `refused_accuracy` both carry a coordinate; the other three
 * carry none, because there was none to carry. The distinction matters when an
 * administrator is deciding whether to close a job: "20 m under a canopy" and
 * "the engineer never granted location permission" are different conversations.
 */
export const fieldCaptureOutcomeSchema = z.enum([
  'accepted',
  'refused_accuracy',
  'no_fix',
  'permission_denied',
  'timeout',
]);
export type FieldCaptureOutcome = z.infer<typeof fieldCaptureOutcomeSchema>;

// ---------------------------------------------------------------------------
// The accuracy policy
// ---------------------------------------------------------------------------

/**
 * The default accuracy threshold, in metres.
 *
 * A number rather than a guess: a phone with a clear sky settles at roughly
 * 3–8 m reported accuracy, the same phone against a wall or under a canopy
 * commonly reports 10–20 m, and a position derived from wifi or the cell
 * network — the thing this threshold exists to refuse — reports 30 m and
 * upward, usually far more. 15 m sits above a real GNSS fix taken at a
 * subscriber's wall and below a network-derived one, so an engineer standing
 * under a tree is not punished while a phone that never saw a satellite is.
 *
 * It is a DEFAULT, not a constant: `PUT /api/field/settings` is the operator's
 * own policy dial, and a dense estate where FATs are ten metres apart will
 * want it tighter than a rural run where they are two hundred.
 */
export const DEFAULT_GPS_ACCURACY_METRES = 15;

/**
 * Bounds on the policy.
 *
 * Below 1 m no consumer device reports honestly, so the floor exists to stop an
 * operator setting a threshold nothing can ever meet and turning every job
 * incomplete. Above 200 m the number has stopped describing a place — that is
 * a circle with a whole estate in it — so a threshold that loose is a policy
 * mistake rather than a lenient policy.
 */
export const GPS_ACCURACY_MIN_METRES = 1;
export const GPS_ACCURACY_MAX_METRES = 200;

// ---------------------------------------------------------------------------
// The checklist (2026-09-16)
// ---------------------------------------------------------------------------

/**
 * What one checklist step asks of the technician at the wall.
 *
 * `tick` — done or not. `light_reading` — a number in dBm, the optical
 * power the technician read at the ONU (typed, or taken from the live
 * reading the phone already holds for the unit). `text` — a short note,
 * for a step whose evidence is a sentence ("router placed on the shelf
 * behind the TV").
 */
export const fieldChecklistKindSchema = z.enum(['tick', 'light_reading', 'text']);
export type FieldChecklistKind = z.infer<typeof fieldChecklistKindSchema>;

/**
 * A step's key is the operator's own stable name for it — lower-case, digits
 * and underscores, so a job's ticks survive a step being relabelled. The
 * label is what the phone and the web print.
 */
export const FIELD_CHECKLIST_KEY_PATTERN = /^[a-z0-9_]{1,40}$/;
export const FIELD_CHECKLIST_MAX_STEPS = 30;

export const fieldChecklistStepSchema = z.object({
  key: z.string().regex(FIELD_CHECKLIST_KEY_PATTERN, 'A step key is lower-case letters, digits and underscores, up to 40 characters.'),
  label: z.string().trim().min(1).max(120),
  kind: fieldChecklistKindSchema,
  /** Required steps left undone are NAMED on the job's requirements; they do not stop a submission. */
  required: z.boolean(),
});
export type FieldChecklistStep = z.infer<typeof fieldChecklistStepSchema>;

/**
 * The operator's step list, unique by key. Bounded because a checklist a
 * technician cannot see the end of is one they stop reading.
 */
export const fieldChecklistStepsSchema = z
  .array(fieldChecklistStepSchema)
  .max(FIELD_CHECKLIST_MAX_STEPS)
  .refine((steps) => new Set(steps.map((s) => s.key)).size === steps.length, {
    message: 'Every checklist step needs its own key — two steps share one.',
  });

/**
 * The default list, in force for an organization that has not written its
 * own. Additive on purpose: an existing settings row without a checklist
 * reads this list, and `checklistIsDefault` says so.
 *
 * The first three are what makes an install an install and are required by
 * default; the last two are the customer-facing finish and are not, because
 * a subscriber who is out when the technician leaves is a fact about the
 * subscriber, not about the fibre.
 */
export const DEFAULT_FIELD_CHECKLIST: readonly FieldChecklistStep[] = [
  { key: 'fibre_terminated', label: 'Fibre terminated and cleaned', kind: 'tick', required: true },
  { key: 'onu_ranging', label: 'ONU powered and ranging', kind: 'tick', required: true },
  { key: 'light_reading', label: 'Light reading at the ONU', kind: 'light_reading', required: true },
  { key: 'wifi_set', label: 'Customer WiFi set', kind: 'tick', required: false },
  { key: 'speed_test_shown', label: 'Customer shown the speed test', kind: 'tick', required: false },
];

/**
 * The band a light reading can honestly sit in, in dBm.
 *
 * A GPON ONU receives somewhere between about −8 dBm (too hot, a short
 * run with no splitter loss) and −27 dBm (the edge of what a class B+ ONU
 * decodes); −40 is below any receiver's floor and +10 above any transmitter.
 * The bounds refuse a reading typed in the wrong unit or with a lost sign —
 * "18.4" for −18.4 dBm — not a reading the platform would disagree with.
 */
export const LIGHT_READING_MIN_DBM = -40;
export const LIGHT_READING_MAX_DBM = 10;

/** True when `dbm` is a finite number a light reading can be. */
export function lightReadingInRange(dbm: number): boolean {
  return Number.isFinite(dbm) && dbm >= LIGHT_READING_MIN_DBM && dbm <= LIGHT_READING_MAX_DBM;
}

export const fieldSettingsSchema = z.object({
  orgId: z.string(),
  /** Fixes reporting worse than this are recorded and REFUSED, never placed. */
  gpsAccuracyMeters: z.number().int().min(GPS_ACCURACY_MIN_METRES).max(GPS_ACCURACY_MAX_METRES),
  /** True when nobody has set a policy and the built-in default is in force. */
  isDefault: z.boolean(),
  updatedAt: z.number().nullable(),
  updatedBy: z.string().nullable(),
  /**
   * Whether the photo step is live at all. Derived from the file store, NOT
   * from anything the org can set: photographs go to object storage and are
   * never mirrored onto this server, so with no S3 configured the step is
   * switched off rather than quietly degraded to a local directory.
   */
  photosEnabled: z.boolean(),
  /** The steps a technician ticks at the wall (2026-09-16). `DEFAULT_FIELD_CHECKLIST` until the operator writes their own. */
  checklist: z.array(fieldChecklistStepSchema),
  /** True while the built-in list is in force. */
  checklistIsDefault: z.boolean(),
});
export type FieldSettings = z.infer<typeof fieldSettingsSchema>;

/**
 * Either dial, or both. The GPS threshold alone was the whole body until
 * 2026-09-16; a body carrying only it still works, and a body carrying only
 * `checklist` leaves the threshold where it is.
 */
export const updateFieldSettingsSchema = z
  .object({
    gpsAccuracyMeters: z
      .number()
      .int()
      .min(GPS_ACCURACY_MIN_METRES)
      .max(GPS_ACCURACY_MAX_METRES)
      .optional(),
    checklist: fieldChecklistStepsSchema.optional(),
  })
  .refine((v) => v.gpsAccuracyMeters !== undefined || v.checklist !== undefined, {
    message: 'Send a GPS threshold, a checklist, or both — an empty change is not a request.',
  });
export type UpdateFieldSettings = z.infer<typeof updateFieldSettingsSchema>;

/**
 * One tick as the phone sends it: `PATCH /api/field/jobs/:id/checklist`
 * body is `{ items: [...] }`. A `light_reading` step ticked done carries
 * its dBm as `value`; a `text` step its sentence; a `tick` step nothing.
 * `done: false` clears whatever was recorded.
 */
export const fieldChecklistTickSchema = z.object({
  key: z.string().min(1).max(40),
  done: z.boolean(),
  value: z.union([z.number(), z.string().max(500)]).nullable().optional(),
});
export type FieldChecklistTick = z.infer<typeof fieldChecklistTickSchema>;

export const patchFieldChecklistSchema = z.object({
  items: z.array(fieldChecklistTickSchema).min(1).max(FIELD_CHECKLIST_MAX_STEPS),
});
export type PatchFieldChecklist = z.infer<typeof patchFieldChecklistSchema>;

/**
 * One step of a job's checklist as the API answers it — the operator's step
 * merged with what the technician recorded against it. Every step of the
 * org's list appears, ticked or not, so a client renders the list without
 * a second read; a step the operator removed after a tick was recorded
 * survives on the job, labelled by its key, because a tick is evidence.
 * Frozen at submission: the list a submitted job carries is the list that
 * was in force, whatever the operator changes afterwards.
 */
export const fieldChecklistEntrySchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: fieldChecklistKindSchema,
  required: z.boolean(),
  done: z.boolean(),
  /** dBm for a light reading, a sentence for text, null otherwise or while undone. */
  value: z.union([z.number(), z.string()]).nullable(),
  /** When it was recorded, epoch ms — null while undone. */
  at: z.number().nullable(),
  /** Who recorded it — a user id, and their name where the platform still knows it. */
  by: z.string().nullable(),
  byName: z.string().nullable(),
});
export type FieldChecklistEntry = z.infer<typeof fieldChecklistEntrySchema>;

/** The least a recorded tick holds, on the platform and in the phone's queue. */
export interface ChecklistRecord {
  key: string;
  done: boolean;
  value: number | string | null;
  at: number | null;
  by: string | null;
}

/**
 * Merge a patch into what is recorded, the way the API does it — so the
 * phone can show the result of a tick still waiting in its queue and be
 * right about it.
 *
 * A key in the patch replaces the record for that key; a key not in the
 * patch is untouched. `done: false` clears the value and the stamp. An
 * item IDENTICAL to what is already recorded (same done, same value) keeps
 * its earlier stamp, which is what makes a replayed patch harmless: the
 * phone re-sends after a lost reply and nothing moves.
 */
export function applyChecklistPatch<T extends ChecklistRecord>(
  current: readonly T[],
  patch: readonly FieldChecklistTick[],
  stamp: { at: number; by: string | null },
): Array<T | ChecklistRecord> {
  const byKey = new Map<string, T | ChecklistRecord>(current.map((c) => [c.key, c] as const));
  for (const item of patch) {
    const value = item.done ? (item.value ?? null) : null;
    const existing = byKey.get(item.key);
    if (existing && existing.done === item.done && existing.value === value) continue;
    byKey.set(item.key, {
      ...(existing ?? {}),
      key: item.key,
      done: item.done,
      value,
      at: item.done ? stamp.at : null,
      by: item.done ? stamp.by : null,
    });
  }
  return [...byKey.values()];
}

/**
 * Whether the required steps are done — one rule for the API's
 * `requirements.checklist` and for the phone's preview of it over ticks
 * still in the queue.
 *
 * `not_required` when the list has no required step at all; `missing`
 * names the required keys left undone, in list order; `met` otherwise.
 */
export function checklistRequirement(
  entries: readonly Pick<FieldChecklistEntry, 'key' | 'required' | 'done'>[],
): { status: 'met' | 'missing' | 'not_required'; missing: string[] } {
  const required = entries.filter((e) => e.required);
  if (required.length === 0) return { status: 'not_required', missing: [] };
  const missing = required.filter((e) => !e.done).map((e) => e.key);
  return { status: missing.length === 0 ? 'met' : 'missing', missing };
}

/** The light reading a job's checklist holds, if any: the latest recorded `light_reading` step's dBm. */
export function lightReadingOf(entries: readonly Pick<FieldChecklistEntry, 'kind' | 'done' | 'value' | 'at'>[]): number | null {
  const readings = entries.filter((e) => e.kind === 'light_reading' && e.done && typeof e.value === 'number');
  if (readings.length === 0) return null;
  readings.sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
  return readings[0]!.value as number;
}

/**
 * An engineer a dispatcher can assign a job to — a member of the organization
 * whose role holds `field.deploy`. The dispatcher picks a person, not a role
 * id, so a job can never land on somebody who cannot open it. Served by
 * `GET /api/field/engineers` for exactly the assignee picker.
 */
export const fieldEngineerSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
});
export type FieldEngineer = z.infer<typeof fieldEngineerSchema>;

// ---------------------------------------------------------------------------
// Captures
// ---------------------------------------------------------------------------

/**
 * A coordinate this platform will store.
 *
 * `accuracyM` is REQUIRED and has no default. That is the whole shape of the
 * rule: a coordinate whose accuracy is unknown cannot be told apart from one
 * somebody typed, so the contract has no way to express one and neither does
 * the table underneath it.
 *
 * There is no `source` field to send. Every capture is a device fix by
 * construction — the column is CHECK-constrained to it — so offering the
 * caller a choice would be offering the manual path this rule forbids.
 */
export const deviceFixSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  /**
   * The device's own reported horizontal accuracy, in metres. Positive: zero
   * or a negative number is a platform that does not know, dressed up as one
   * that does.
   */
  accuracyM: z.number().positive().max(100_000),
  /** Epoch ms from the DEVICE, which is what makes a day-late sync legible. */
  capturedAt: z.number().int().positive(),
});
export type DeviceFix = z.infer<typeof deviceFixSchema>;

/** Why the device produced no coordinate at all. */
export const fixFailureSchema = z.enum(['no_fix', 'permission_denied', 'timeout']);
export type FixFailure = z.infer<typeof fixFailureSchema>;

export const fieldCaptureSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  outcome: fieldCaptureOutcomeSchema,
  /** Null on the three outcomes that carry no coordinate. */
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  accuracyM: z.number().nullable(),
  /** The policy this capture was judged against, snapshotted at judgement. */
  thresholdM: z.number().int(),
  capturedAt: z.number(),
  receivedAt: z.number(),
  note: z.string().nullable(),
});
export type FieldCapture = z.infer<typeof fieldCaptureSchema>;

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------

export const fieldPhotoSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  kind: fieldPhotoKindSchema,
  contentType: z.string(),
  bytes: z.number().int(),
  sha256: z.string(),
  uploadedAt: z.number(),
  uploadedBy: z.string().nullable(),
});
export type FieldPhoto = z.infer<typeof fieldPhotoSchema>;

/** Image types the platform will take. Anything else is a file, not a photo. */
export const FIELD_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type FieldPhotoContentType = (typeof FIELD_PHOTO_CONTENT_TYPES)[number];

/** 16 MB. A modern phone's full-resolution JPEG fits with room to spare. */
export const FIELD_PHOTO_MAX_BYTES = 16 * 1024 * 1024;

// ---------------------------------------------------------------------------
// The job
// ---------------------------------------------------------------------------

/**
 * Whether each half of the deployment was satisfied, derived at read time from
 * the capture and photo rows.
 *
 * Derived rather than stored on purpose: an `incompleteReason` column plus a
 * stored list of unmet requirements is two records of one fact, and the day
 * they disagree is the day nobody can say what actually happened on site. The
 * rows they are derived from are themselves frozen — a capture keeps the
 * outcome and the threshold it was judged against, and neither a completed nor
 * a closed job accepts further photos.
 */
export const fieldRequirementsSchema = z.object({
  /**
   * `met` — a fix inside the policy.
   * `refused` — a fix arrived and was outside it; the accuracy is on the capture.
   * `missing` — no coordinate at all, or nothing submitted yet.
   */
  gps: z.enum(['met', 'refused', 'missing']),
  /**
   * `disabled` is not a failure: the photo step is off because this platform
   * has no object store, and a job must not be impossible to complete because
   * of a setting the engineer cannot see or change.
   */
  photos: z.enum(['met', 'missing', 'not_required', 'disabled']),
  /**
   * The checklist (2026-09-16). `missing` names the required steps left
   * undone in `checklistMissing`; `not_required` when the list has no
   * required step. It NAMES, it does not hold: a required step left undone
   * is on the record beside the outcome, and the outcome is still decided
   * by the fix and the photograph — an operator's default list must not turn
   * every job in flight incomplete the morning it lands. Optional in the
   * schema because a job saved on a handset by an older build has neither.
   */
  checklist: z.enum(['met', 'missing', 'not_required']).optional(),
  checklistMissing: z.array(z.string()).optional(),
});
export type FieldRequirements = z.infer<typeof fieldRequirementsSchema>;

export const fieldJobSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  title: z.string(),
  status: fieldJobStatusSchema,
  targetType: fieldTargetTypeSchema,
  targetId: z.string(),
  /**
   * The target as a person would say it out loud — "ONU 42 (ZXICCD54C047)",
   * "FAT Kikuyu-3". A picker that offers row ids is a picker nobody can use,
   * so this is part of the contract rather than left to each client.
   */
  targetLabel: z.string(),
  /** Which photo this target owes, or null when it owes none. */
  requiredPhotoKind: fieldPhotoKindSchema.nullable(),
  assignedTo: z.string().nullable(),
  assignedToName: z.string().nullable(),
  /**
   * The accuracy policy in force when this job was submitted, frozen. A policy
   * changed next month must not rewrite whether last month's job passed.
   */
  accuracyThresholdM: z.number().int().nullable(),
  /**
   * What the photo step was at submission: `required`, `not_required`, or
   * `disabled` because there was no object store. Without it, a completed ONU
   * job with no photographs is unreadable a year later.
   */
  photoStep: z.enum(['required', 'not_required', 'disabled']).nullable(),
  incompleteReason: fieldIncompleteReasonSchema.nullable(),
  requirements: fieldRequirementsSchema,
  /** Every capture, newest first — including the refused ones. */
  captures: z.array(fieldCaptureSchema),
  photos: z.array(fieldPhotoSchema),
  /**
   * The checklist (2026-09-16): every step of the operator's list with what
   * the technician recorded against it — see `fieldChecklistEntrySchema`.
   * Optional so a job saved by an older build still parses; the API always
   * sends it.
   */
  checklist: z.array(fieldChecklistEntrySchema).optional(),
  /** The light reading recorded at the ONU, in dBm — the checklist's `light_reading` step, surfaced as evidence. */
  lightReadingDbm: z.number().nullable().optional(),
  note: z.string().nullable(),
  submittedAt: z.number().nullable(),
  submittedBy: z.string().nullable(),
  closedAt: z.number().nullable(),
  closedBy: z.string().nullable(),
  closedByName: z.string().nullable(),
  closeReason: z.string().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type FieldJob = z.infer<typeof fieldJobSchema>;

/**
 * `clientKey` is the offline story's half of the contract.
 *
 * A phone that syncs the day's work, loses the reply and syncs again must not
 * create two jobs or two coordinates. The key is the client's, opaque to the
 * platform, and unique per organization; a create that repeats one returns the
 * job that already exists rather than a second one.
 */
export const createFieldJobSchema = z.object({
  title: z.string().min(1).max(200),
  targetType: fieldTargetTypeSchema,
  targetId: z.string().min(1).max(80),
  assignedTo: z.string().min(1).max(80),
  clientKey: z.string().min(1).max(120).optional(),
});
export type CreateFieldJob = z.infer<typeof createFieldJobSchema>;

/**
 * One submission from the field.
 *
 * Exactly one of `fix` and `failure`: a coordinate, or the reason there is
 * none. Both would be a contradiction and neither would be a submission that
 * says nothing.
 *
 * `clientKey` is REQUIRED here, unlike on create — this is the call a phone
 * replays, and the one that must not produce a second coordinate.
 */
export const submitFieldJobSchema = z
  .object({
    clientKey: z.string().min(1).max(120),
    fix: deviceFixSchema.optional(),
    failure: fixFailureSchema.optional(),
    note: z.string().max(2000).optional(),
  })
  .refine((v) => (v.fix === undefined) !== (v.failure === undefined), {
    message:
      'Send either a device fix or the reason there was none — never both, and never neither.',
  });
export type SubmitFieldJob = z.infer<typeof submitFieldJobSchema>;

/**
 * Closing an incomplete job.
 *
 * The reason is required and has a floor, because this is the act that ends a
 * deployment nobody could finish properly. "ok" is not a record; "FAT is inside
 * a locked yard, coordinates taken at the gate, verified against the as-built"
 * is.
 */
export const closeFieldJobSchema = z.object({
  reason: z.string().min(10).max(2000),
});
export type CloseFieldJob = z.infer<typeof closeFieldJobSchema>;

/**
 * Reassigning or cancelling an open job — `PATCH /api/field/jobs/:id`, under
 * `field.manage`.
 *
 * Send `assignedTo` to hand the job to a different engineer (the API resets the
 * status to `assigned` — a fresh assignment — and re-runs createJob's check that
 * the new assignee is an org member holding `field.deploy`). Send
 * `status: 'cancelled'` to call the job off. Only the two open states —
 * `assigned` and `in_progress` — accept either act; the API refuses on
 * complete/incomplete/closed/cancelled. `cancelled` is the only status the
 * caller may set here, which is why it is a literal and not the whole enum.
 *
 * At least one field is required: an empty body is not a request.
 */
export const updateFieldJobSchema = z
  .object({
    assignedTo: z.string().min(1).max(80).optional(),
    status: z.literal('cancelled').optional(),
  })
  .refine((v) => v.assignedTo !== undefined || v.status !== undefined, {
    message:
      'Send a new assignee to reassign, or status "cancelled" to cancel — an empty change is not a request.',
  })
  .refine((v) => !(v.assignedTo !== undefined && v.status !== undefined), {
    message: 'Reassign or cancel in separate requests — not both in one.',
  });
export type UpdateFieldJob = z.infer<typeof updateFieldJobSchema>;

/**
 * Whether a capture inside `thresholdM` should be accepted.
 *
 * One implementation, in shared, for the same reason the zone geometry lives
 * here: the phone refuses a bad fix before it wastes an engineer's walk back to
 * the van, and the API refuses it again on arrival. If those two used different
 * comparisons the app would offer to submit what the platform then rejects.
 */
export function fixMeetsPolicy(accuracyM: number, thresholdM: number): boolean {
  return Number.isFinite(accuracyM) && accuracyM > 0 && accuracyM <= thresholdM;
}

/**
 * Whether a job still keeps its ONU on the technician's phone — the
 * `onu.read_assigned` window, decided once for the API and the app.
 *
 * A technician holds no fleet-wide read; what they may open is the ONU on a
 * deployment job assigned to them, and "assigned" has an edge in time. The
 * API (`services/field/assigned-scope.ts`) computes the set of ONU ids from
 * this rule; the phone's job screen asks it before offering "Open this ONU",
 * so a job whose window has closed says so instead of linking to a 404. Two
 * copies of the rule would drift on exactly the day it matters — the job
 * completed fifteen days ago — so there is one.
 *
 * The rule: not cancelled (a job called off is a customer the technician was
 * never sent to, whatever the row says about who held it), and either still
 * open (`assigned` / `in_progress`) or finished within the last FOURTEEN
 * DAYS. The fortnight is a decision, not a derivation: a job that completed
 * yesterday is a subscriber who may ring tomorrow — "the WiFi is not
 * working" — and the technician who installed it is who gets sent back, often
 * without a new job being raised. Two weeks covers that follow-up without
 * leaving every subscriber a technician ever touched on their phone forever.
 * "Finished" is the moment the job stopped being open: `closedAt` for a
 * closed job, else `submittedAt` for a complete or incomplete one, else
 * `updatedAt` for a row that carries neither (a status set by hand).
 */
export const ASSIGNED_FOLLOW_UP_WINDOW_MS = 14 * 86_400_000;

/** The least a job has to say for the window to be judged. */
export interface AssignedWindowJob {
  status: FieldJobStatus;
  closedAt: number | null;
  submittedAt: number | null;
  updatedAt: number;
}

const OPEN_JOB_STATUSES: readonly FieldJobStatus[] = ['assigned', 'in_progress'];

/** When a job stopped being open — null while it still is. */
export function jobFinishedAt(job: AssignedWindowJob): number | null {
  if (OPEN_JOB_STATUSES.includes(job.status)) return null;
  return job.closedAt ?? job.submittedAt ?? job.updatedAt;
}

export function jobKeepsOnuAssigned(job: AssignedWindowJob, now: number = Date.now()): boolean {
  if (job.status === 'cancelled') return false;
  const finished = jobFinishedAt(job);
  return finished === null || finished >= now - ASSIGNED_FOLLOW_UP_WINDOW_MS;
}
